import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LayoutProvider, useLayout } from './context/LayoutContext';
import { LoginView } from './components/auth/LoginView';
import { Navbar } from './components/layout/Navbar';
import { Sidebar } from './components/layout/Sidebar';
import { RecordsManagerView } from './components/transparency/RecordsManagerView';
import { PagesAdminView } from './components/transparency/PagesAdminView';
import { SmartZipImporterView } from './components/datahub/SmartZipImporterView';
import { WebScraperView } from './components/datahub/WebScraperView';
import { PersonnelManagerView } from './components/personnel/PersonnelManagerView';
import { UserAdminView } from './components/security/UserAdminView';
import { AuditLogView } from './components/security/AuditLogView';
import { ComplianceDashboardView } from './components/compliance/ComplianceDashboardView';
import { BrandingSettingsView } from './components/platform/BrandingSettingsView';
import { TenantsAdminView } from './components/platform/TenantsAdminView';
import { QuickWizardModal } from './components/transparency/QuickWizardModal';
import { SyncManagerModal } from './components/sync/SyncManagerModal';
import { EnvSyncWizard } from './components/sync/EnvSyncWizard';
import { AnnouncementBar } from './components/announcements/Announcements';
import { SystemAnnouncementsView } from './components/platform/SystemAnnouncementsView';
import { ExportToolsModal } from './components/datahub/ExportToolsModal';
import { PublicPortalView } from './components/public/PublicPortalView';
import { QuotaBanner } from './components/common/QuotaBanner';
import { GlobalMonitoringView } from './components/monitoring/GlobalMonitoringView';
import { ResetPasswordView } from './components/auth/ResetPasswordView';
import { UserProfileModal } from './components/security/UserProfileModal';
import { RegulationsAdminView } from './components/compliance/RegulationsAdminView';
import { LinkedEntitiesView } from './components/personnel/LinkedEntitiesView';
import { AccessGroupsView } from './components/personnel/AccessGroupsView';
import { TextReplacerView } from './components/datahub/TextReplacerView';
import { UploadPolicyAndStatsView } from './components/platform/UploadPolicyAndStatsView';
import { SandboxBanner } from './components/sandbox/SandboxBanner';
import { BackupRestoreView } from './components/system/BackupRestoreView';
import { SchemaMaintenanceView } from './components/system/SchemaMaintenanceView';
import { LockdownPanel } from './components/security/LockdownPanel';
import { SelfhostInstancesView } from './components/system/SelfhostInstancesView';
import { MaintenanceOverlay } from './components/system/MaintenanceOverlay';
import { TicketsView } from './components/tickets/TicketsView';
import { HelpCenterModal } from './components/help/HelpCenterModal';
import { MenuConfiguratorModal } from './components/layout/MenuConfiguratorModal';
import { useGlobalEscapeClose } from './hooks/useGlobalEscapeClose';
import { ScrollToTopButton } from './components/common/ScrollToTopButton';
import { ApiClient } from './api/client';
import type { PageItem } from './types';
import { RefreshCw, Download, ShieldAlert } from 'lucide-react';

import { SystemBrandingProvider, useSystemBranding } from './context/SystemBrandingContext';

const MainLayout: React.FC = () => {
  const { user, tenant, isLoading: authLoading, isImpersonating, stopImpersonation } = useAuth();
  const { activeModuleId, setActiveModule, setActivePageId, canAccessModule, isConfiguratorOpen, setIsConfiguratorOpen } = useLayout();
  useGlobalEscapeClose(); // ESC: a legfelső nyitott ablak bezárása
  const { branding } = useSystemBranding();

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return true;
  });
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isSyncOpen, setIsSyncOpen] = useState(false);
  const [isLegacySyncOpen, setIsLegacySyncOpen] = useState(false);

  // A monitor oldal és más modulok ezzel az eseménnyel nyithatják meg a szinkron varázslót
  useEffect(() => {
    const open = () => setIsSyncOpen(true);
    window.addEventListener('kozad:open-sync', open);
    return () => window.removeEventListener('kozad:open-sync', open);
  }, []);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [pages, setPages] = useState<PageItem[]>([]);

  useEffect(() => {
    const sysName = branding.system_title || 'KözzéTér Pro.';
    if (tenant?.name) {
      document.title = `${sysName} – ${tenant.name}`;
    } else {
      document.title = `${sysName} – ${branding.subtitle || 'Közzétételi Platform'}`;
    }
  }, [branding.system_title, branding.subtitle, tenant?.name]);

  useEffect(() => {
    if (user) {
      ApiClient.getPages().then((res) => {
        if (res.ok && res.data) {
          setPages(res.data);
        }
      });
    }
  }, [user, tenant?.id]);

  // Publikus Portál inaktivitási időzítő bejelentkezett admin esetén (mindig a korai return-ök előtt hívandó meg!)
  useEffect(() => {
    if (activeModuleId !== 'public_portal' || !user) return;

    const timeoutSec = Number(branding.public_portal_timeout) > 0 ? Number(branding.public_portal_timeout) : 30;
    let timer: any;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setActiveModule('records_manager');
      }, timeoutSec * 1000);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(ev => window.removeEventListener(ev, resetTimer));
    };
  }, [activeModuleId, user, branding.public_portal_timeout]);

  if (authLoading) {
    return (
      <div className="min-h-screen w-full bg-slate-50 dark:bg-[#0b0f1a] flex flex-col items-center justify-center text-slate-500 gap-3">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="text-sm font-medium">Közfeladat Rendszer betöltése...</span>
      </div>
    );
  }

  const searchParams = new URLSearchParams(window.location.search);
  const resetToken = searchParams.get('reset_token');

  if (resetToken) {
    return (
      <ResetPasswordView
        token={resetToken}
        onSuccess={() => {
          // Clean reset_token from URL and switch back to normal view
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          window.location.href = cleanUrl;
        }}
      />
    );
  }

  // Publikus Portál detektálás:
  // 1. Hostname alapján (pl. kpub.example.hu)
  // 2. /public vagy /kpub előtagú útvonalak, vagy ?t= query paraméter
  // 3. Tiszta szervezeti slug útvonalak (pl. /gyal vagy /minta-hivatal), kizárva a belső védett útvonalakat
  const hostname = window.location.hostname.toLowerCase();
  const isKpubHost = hostname.startsWith('kpub.') || hostname.includes('.kpub.');
  const pathname = window.location.pathname;
  const pathSegments = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  const reservedPaths = ['login', 'admin', 'app', 'tenants', 'reset-password', 'help', 'api'];
  const hasCustomSlugPath = pathSegments.length > 0 && !reservedPaths.includes(pathSegments[0]);

  const isPublicUrl = 
    isKpubHost || 
    pathname.startsWith('/public') || 
    pathname.startsWith('/kpub') || 
    searchParams.has('t') ||
    hasCustomSlugPath;

  if (!user && isPublicUrl) {
    return <PublicPortalView onBackToAdmin={() => { window.location.href = '/'; }} />;
  }

  if (!user) {
    return <LoginView />;
  }

  // Publikus Portál nézet bejelentkezett felhasználó esetén: tiszta, teljes képernyős nézet (admin menük nélkül)
  if (user && activeModuleId === 'public_portal') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#070a12] text-slate-900 dark:text-slate-100 flex flex-col font-sans">
        <PublicPortalView onBackToAdmin={() => setActiveModule('records_manager')} />
      </div>
    );
  }

  // Dynamic Content Router based on Active Module
  const renderActiveModule = () => {
    if (!canAccessModule(activeModuleId)) {
      return (
        <div className="p-8 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-xl mx-auto space-y-4 shadow-xs mt-12">
          <ShieldAlert className="w-12 h-12 text-rose-600 dark:text-rose-400 mx-auto" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Hozzáférés Megtagadva</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Ehhez a felülethez a jelenlegi szerepkörével nem rendelkezik hozzáférési jogosultsággal.
          </p>
          <button
            onClick={() => setActiveModule('records_manager')}
            className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium text-sm shadow-md transition cursor-pointer"
          >
            Vissza az Adatlapokhoz
          </button>
        </div>
      );
    }

    switch (activeModuleId) {
      case 'ticket_management':
        return <TicketsView />;
      case 'public_portal':
        return <PublicPortalView onBackToAdmin={() => setActiveModule('records_manager')} />;
      case 'pages_admin':
        return <PagesAdminView />;
      case 'smart_zip_importer':
        return <SmartZipImporterView />;
      case 'web_scraper':
        return <WebScraperView />;
      case 'personnel_manager':
        return <PersonnelManagerView />;
      case 'linked_entities':
        return <LinkedEntitiesView />;
      case 'access_groups':
        return <AccessGroupsView />;
      case 'compliance_dashboard':
        return <ComplianceDashboardView onOpenWizard={() => setIsWizardOpen(true)} />;
      case 'regulations_admin':
        return <RegulationsAdminView />;
      case 'user_admin':
        return <UserAdminView />;
      case 'audit_log':
        return <AuditLogView />;
      case 'branding_settings':
        return <BrandingSettingsView />;
      case 'tenants_admin':
        return <TenantsAdminView />;
      case 'text_replacer':
        return <TextReplacerView />;
      case 'upload_policy':
        return <UploadPolicyAndStatsView />;
      case 'export_tools':
        return (
          <div className="p-8 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl max-w-xl mx-auto space-y-4 shadow-xs">
            <Download className="w-12 h-12 text-indigo-600 dark:text-indigo-400 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Adat Export Központ</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Kattintson az alábbi gombra az XLSX, Közadat CSV és Mappasablon exportáló panel megnyitásához.
            </p>
            <button
              onClick={() => setIsExportOpen(true)}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium text-sm shadow-md transition cursor-pointer"
            >
              Export Panel Megnyitása
            </button>
          </div>
        );
      case 'system_monitoring':
        return <GlobalMonitoringView />;
      case 'sync_manager':
        return <GlobalMonitoringView initialTab="sync" />;
      case 'backup_restore':
        return <BackupRestoreView />;
      case 'schema_maintenance':
        return <SchemaMaintenanceView />;
      case 'lockdown_panel':
        return <LockdownPanel />;
      case 'system_announcements':
        return <SystemAnnouncementsView />;
      case 'selfhost_instances':
        // B7: korábban nem volt ága, ezért a bejegyzéslista nyílt meg helyette.
        return <SelfhostInstancesView />;
      case 'records_manager':
      default:
        return <RecordsManagerView />;
    }
  };

  return (
    <div className="h-screen w-full overflow-hidden bg-slate-50 dark:bg-[#070a12] text-slate-900 dark:text-slate-100 flex flex-col font-sans transition-colors duration-200 selection:bg-indigo-500/20 selection:text-indigo-900 dark:selection:text-indigo-200">
      {/* Maintenance Mode Overlay & Warning */}
      <MaintenanceOverlay />

      {/* Sandbox (DEMO) Mode Banner */}
      <SandboxBanner />

      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 text-white px-4 py-2 text-xs sm:text-sm font-semibold flex items-center justify-between shadow-md z-50 animate-in fade-in shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-base">🎭</span>
            <span>
              <strong>Megszemélyesítés aktív:</strong> Belépve mint <span className="underline decoration-white/50 underline-offset-2">{tenant?.name || 'Szervezet'}</span> (/{tenant?.slug}) megbízás alapján
            </span>
          </div>
          <button
            onClick={stopImpersonation}
            className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 active:bg-white/40 text-white rounded-lg transition font-bold text-xs border border-white/40 cursor-pointer shadow-xs whitespace-nowrap"
          >
            <span>↩ Visszatérés a Főadminisztrációhoz</span>
          </button>
        </div>
      )}

      {/* Top Navigation */}
      <Navbar
        onOpenWizard={() => setIsWizardOpen(true)}
        onOpenSync={() => setIsSyncOpen(true)}
        onOpenProfile={() => setIsProfileOpen(true)}
        onOpenExport={() => setIsExportOpen(true)}
        onOpenHelp={() => setIsHelpOpen(true)}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Rendszerüzenetek: rögzített sáv és/vagy fényújság (superadmin állítja) */}
      <AnnouncementBar tenantKey={tenant?.id} />

      <div className="flex-1 flex overflow-hidden min-w-0 relative">
        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />

        {/* Mobile/Tablet backdrop when sidebar is open */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-slate-950/40 z-20 lg:hidden backdrop-blur-2xs transition-opacity"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 p-3 sm:p-4 lg:p-5 overflow-y-auto overflow-x-hidden max-w-full w-full">
          {/* kvóta-figyelmeztetés (a küszöb felett; a Lomtár és az Archívum is beleszámít) */}
          <QuotaBanner refreshKey={activeModuleId} />
          {renderActiveModule()}
          {/* Hely a „Tetejére” gombnak: a lista utolsó sora is fölé görgethető, így nem takarja ki */}
          <div aria-hidden="true" className="h-16 shrink-0" />
        </main>
      </div>

      {/* Lebegő "Vissza a tetejére" gomb minden admin nézethez (Jogszabálygyűjtemény, Adatlapok, Napló stb.) */}
      <ScrollToTopButton containerSelector="main" />

      {/* Global Modals */}
      <QuickWizardModal
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        pages={pages}
        onSuccess={(targetPageId, newRecordId) => {
          setIsWizardOpen(false);
          setActivePageId(targetPageId);
          setActiveModule('records_manager');
          if (newRecordId) {
            sessionStorage.setItem('kf_jump_record_id', String(newRecordId));
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('kf_jump_to_record', {
                detail: { pageId: targetPageId, recordId: newRecordId }
              }));
            }, 60);
          }
        }}
      />

      <EnvSyncWizard
        isOpen={isSyncOpen}
        onClose={() => setIsSyncOpen(false)}
        onOpenLegacy={() => { setIsSyncOpen(false); setIsLegacySyncOpen(true); }}
      />

      {/* Régi, csomag alapú szinkron – megtartva (Zero-Drop), a varázslóból nyitható */}
      <SyncManagerModal
        isOpen={isLegacySyncOpen}
        onClose={() => setIsLegacySyncOpen(false)}
      />

      <ExportToolsModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
      />

      <UserProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />

      <HelpCenterModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />

      <MenuConfiguratorModal
        isOpen={isConfiguratorOpen}
        onClose={() => setIsConfiguratorOpen(false)}
      />
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <SystemBrandingProvider>
        <AuthProvider>
          <LayoutProvider>
            <MainLayout />
          </LayoutProvider>
        </AuthProvider>
      </SystemBrandingProvider>
    </ThemeProvider>
  );
}
